import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import {
  getSanctioningBodies,
  createSanctioningBody,
  updateSanctioningBody,
  deleteSanctioningBody,
  getSanctioningBodyUsers,
  getUsers,
  assignSanctioningBodyToUser,
  removeSanctioningBodyFromUser,
} from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import AssignmentList from '../../components/admin/AssignmentList';
import type { AssignedUser, SanctioningBody, UserWithRoles } from '../../types';
import { PageHeader, Alert, Badge, Button, DataTable, Field, Input, Label, Separator, Switch, Textarea, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@racing/ui';

const SanctioningBodiesPage = () => {
  const [bodies, setBodies] = useState<SanctioningBody[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<SanctioningBody>>({});
  const [isEdit, setIsEdit] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [allUsers, setAllUsers] = useState<UserWithRoles[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const loadBodies = useCallback(async () => {
    try {
      setLoading(true);
      setBodies(await getSanctioningBodies());
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBodies();
  }, [loadBodies]);

  const loadAssignments = useCallback(async (sbId: number) => {
    try {
      setUsersLoading(true);
      const [assigned, all] = await Promise.all([getSanctioningBodyUsers(sbId), getUsers()]);
      setAssignedUsers(assigned);
      setAllUsers(all);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const openEdit = (body: SanctioningBody) => {
    setEditing({ ...body });
    setIsEdit(true);
    setDialogOpen(true);
    setAssignedUsers([]);
    loadAssignments(body.sanctioning_body_id);
  };

  const handleSave = async () => {
    try {
      if (isEdit && editing.sanctioning_body_id) {
        await updateSanctioningBody(editing.sanctioning_body_id, editing);
      } else {
        await createSanctioningBody(editing);
      }
      setDialogOpen(false);
      loadBodies();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (pendingDeleteId === null) return;
    try {
      await deleteSanctioningBody(pendingDeleteId);
      loadBodies();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
    setPendingDeleteId(null);
  };

  const availableUsers = useMemo(() => {
    const assignedIds = new Set(assignedUsers.map((u) => u.user_id));
    return allUsers.filter(
      (user) =>
        user.roles?.some((role) => role.role_name === 'SANCTIONING_BODY') &&
        !assignedIds.has(user.id)
    );
  }, [allUsers, assignedUsers]);

  const columns = useMemo<ColumnDef<SanctioningBody, any>[]>(
    () => [
      { accessorKey: 'body_name', header: 'Name', meta: { width: 220 } },
      { accessorKey: 'abbreviation', header: 'Abbreviation', meta: { width: 140 } },
      { accessorKey: 'contact_email', header: 'Email', meta: { width: 200, priority: 'md' } },
      { accessorKey: 'contact_phone', header: 'Phone', meta: { width: 150, priority: 'lg' } },
      {
        accessorKey: 'is_active',
        header: 'Active',
        cell: ({ getValue }) =>
          getValue<boolean>() !== false ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="neutral">Inactive</Badge>
          ),
        meta: { width: 110, priority: 'sm' },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Delete ${row.original.body_name}`}
            className="text-danger hover:bg-danger-bg"
            onClick={(event) => {
              event.stopPropagation();
              setPendingDeleteId(row.original.sanctioning_body_id);
              setConfirmOpen(true);
            }}
          >
            <Trash2 />
          </Button>
        ),
        meta: { width: 60, incidental: true },
      },
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col">
      <PageHeader
        title="Sanctioning Bodies"
        subtitle="Organisations that sanction events. Assigning a user grants them access to that body's events."
        actions={
          <Button
            className="w-full sm:w-auto"
            onClick={() => {
              setEditing({ is_active: true });
              setIsEdit(false);
              setDialogOpen(true);
            }}
          >
            <Plus /> Add Sanctioning Body
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DataTable
        data={bodies}
        columns={columns}
        getRowId={(row) => String(row.sanctioning_body_id)}
        loading={loading}
        onRowClick={openEdit}
        emptyMessage="No sanctioning bodies yet"
        pageSizeOptions={[10, 25, 50]}
        className="h-[calc(100dvh-16rem)] min-h-[22rem]"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? 'Edit sanctioning body' : 'Add sanctioning body'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" required className="sm:col-span-2">
                {(p) => (
                  <Input
                    value={editing.body_name || ''}
                    onChange={(e) => setEditing({ ...editing, body_name: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="Abbreviation">
                {(p) => (
                  <Input
                    value={editing.abbreviation || ''}
                    onChange={(e) => setEditing({ ...editing, abbreviation: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="Website">
                {(p) => (
                  <Input
                    type="url"
                    value={editing.website || ''}
                    onChange={(e) => setEditing({ ...editing, website: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="Contact Email">
                {(p) => (
                  <Input
                    type="email"
                    value={editing.contact_email || ''}
                    onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="Contact Phone">
                {(p) => (
                  <Input
                    type="tel"
                    value={editing.contact_phone || ''}
                    onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="Notes" className="sm:col-span-2">
                {(p) => (
                  <Textarea
                    rows={3}
                    value={editing.notes || ''}
                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch
                  id="sb-active"
                  checked={editing.is_active !== false}
                  onCheckedChange={(checked) => setEditing({ ...editing, is_active: checked })}
                />
                <Label htmlFor="sb-active">Active</Label>
              </div>
            </div>

            {isEdit && editing.sanctioning_body_id && (
              <>
                <Separator />
                <section className="flex flex-col gap-3">
                  <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
                    Assigned users
                  </h3>
                  <AssignmentList
                    items={assignedUsers.map((user) => ({
                      id: user.user_id,
                      primary: user.display_name || user.username,
                      secondary: user.display_name ? user.username : null,
                    }))}
                    options={availableUsers.map((user) => ({
                      id: user.id,
                      label: user.display_name || user.username,
                    }))}
                    addLabel="Add user"
                    emptyMessage="No users assigned. Nobody can manage events here until someone is."
                    loading={usersLoading}
                    onAdd={async (userId: number) => {
                      try {
                        await assignSanctioningBodyToUser(userId, editing.sanctioning_body_id!);
                        loadAssignments(editing.sanctioning_body_id!);
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                    onRemove={async (userId: number) => {
                      try {
                        await removeSanctioningBodyFromUser(userId, editing.sanctioning_body_id!);
                        loadAssignments(editing.sanctioning_body_id!);
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                  />
                </section>
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete sanctioning body?"
        message="Events already sanctioned by this body are not deleted, but it will no longer be selectable."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDeleteId(null);
        }}
      />
    </div>
  );
};

export default SanctioningBodiesPage;
